/**
 * FpsSkinLocker — Phase 12
 * Full-screen locker panel for equipping character skins, weapon wraps,
 * pickaxes, and gliders. Persists selections to localStorage.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SkinCategory = 'skin' | 'wrap' | 'pickaxe' | 'glider';

export interface SkinLoadout {
  skinId: string;
  wrapId: string;
  pickaxeId: string;
  gliderId: string;
}

// ---------------------------------------------------------------------------
// Catalogue definitions
// ---------------------------------------------------------------------------

interface SkinItem {
  id: string;
  label: string;
  hue: number;
  swatch: string; // CSS color for preview swatch
}

interface WrapItem {
  id: string;
  label: string;
  color: string;
}

interface PickaxeItem {
  id: string;
  label: string;
  swatch: string;
}

interface GliderItem {
  id: string;
  label: string;
  swatch: string;
}

const SKINS: SkinItem[] = [
  { id: 'default',  label: 'Default',        hue: 0,    swatch: '#c0614a' },
  { id: 'azure',    label: 'Azure Ranger',   hue: 0.6,  swatch: '#2a7fff' },
  { id: 'jade',     label: 'Jade Warrior',   hue: 0.33, swatch: '#3aaf50' },
  { id: 'sunset',   label: 'Sunset Striker', hue: 0.08, swatch: '#e87820' },
  { id: 'void',     label: 'Void Walker',    hue: 0.75, swatch: '#9040d0' },
  { id: 'prism',    label: 'Prism Surge',    hue: 0.85, swatch: '#e030b0' },
];

const WRAPS: WrapItem[] = [
  { id: 'default_wrap', label: 'Default',     color: '#888888' },
  { id: 'gold_wrap',    label: 'Gold Wrap',   color: '#ffd700' },
  { id: 'carbon_wrap',  label: 'Carbon Fiber',color: '#222222' },
  { id: 'camo_wrap',    label: 'Camo',        color: '#4a7a3a' },
];

const PICKAXES: PickaxeItem[] = [
  { id: 'default_axe', label: 'Default Pickaxe', swatch: '#888888' },
  { id: 'rainbow_axe', label: 'Rainbow Smash',   swatch: 'linear-gradient(135deg,#f00,#ff0,#0f0,#0ff,#00f,#f0f)' },
  { id: 'star_axe',    label: 'Star Wand',        swatch: '#ffd700' },
];

const GLIDERS: GliderItem[] = [
  { id: 'default_glider', label: 'Standard Glider', swatch: '#aaddff' },
  { id: 'golden_glider',  label: 'Golden Eagle',    swatch: '#ffd700' },
  { id: 'neon_glider',    label: 'Neon Rider',      swatch: '#00ffcc' },
];

// ---------------------------------------------------------------------------
// LocalStorage keys
// ---------------------------------------------------------------------------

const LS_SKIN    = 'fps_skin_id';
const LS_WRAP    = 'fps_wrap_id';
const LS_PICKAXE = 'fps_pickaxe_id';
const LS_GLIDER  = 'fps_glider_id';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hslFromHue(hue: number): string {
  return `hsl(${Math.round(hue * 360)},65%,48%)`;
}

// ---------------------------------------------------------------------------
// FpsSkinLocker
// ---------------------------------------------------------------------------

export class FpsSkinLocker {
  private overlay: HTMLDivElement;
  private visible = false;
  private activeCategory: SkinCategory = 'skin';

  // Currently highlighted (not yet equipped) selection per tab
  private pendingSkin    = localStorage.getItem(LS_SKIN)    ?? 'default';
  private pendingWrap    = localStorage.getItem(LS_WRAP)    ?? 'default_wrap';
  private pendingPickaxe = localStorage.getItem(LS_PICKAXE) ?? 'default_axe';
  private pendingGlider  = localStorage.getItem(LS_GLIDER)  ?? 'default_glider';

  private loadoutCbs: Array<(l: SkinLoadout) => void> = [];

  // DOM refs rebuilt each render
  private gridEl!: HTMLDivElement;
  private previewEl!: HTMLDivElement;
  private equipBtn!: HTMLButtonElement;

  constructor(private mount: HTMLElement) {
    this.overlay = document.createElement('div');
    this.overlay.style.cssText = [
      'position:fixed;inset:0;z-index:400',
      'background:transparent',
      'pointer-events:none',
      'display:none;flex-direction:row',
      'font-family:"Trebuchet MS","Segoe UI",system-ui,sans-serif',
      'color:#fff',
    ].join(';');

    this.overlay.innerHTML = this.buildShell();
    document.body.appendChild(this.overlay);

    this.gridEl    = this.overlay.querySelector('#sl-grid')    as HTMLDivElement;
    this.previewEl = this.overlay.querySelector('#sl-preview') as HTMLDivElement;
    this.equipBtn  = this.overlay.querySelector('#sl-equip')   as HTMLButtonElement;

    this.bindEvents();
    this.renderGrid();
    this.renderPreview();
  }

  // ---------------------------------------------------------------------------
  // Shell HTML
  // ---------------------------------------------------------------------------

  private buildShell(): string {
    const tabs: Array<{ cat: SkinCategory; label: string }> = [
      { cat: 'skin',    label: 'Skins' },
      { cat: 'wrap',    label: 'Wraps' },
      { cat: 'pickaxe', label: 'Pickaxes' },
      { cat: 'glider',  label: 'Gliders' },
    ];

    const tabsHtml = tabs.map(t =>
      `<button data-cat="${t.cat}" id="sl-tab-${t.cat}" style="` +
      'display:block;width:100%;padding:14px 18px;margin-bottom:6px;' +
      'border:none;border-radius:8px;text-align:left;cursor:pointer;' +
      'font:700 13px system-ui;letter-spacing:0.06em;' +
      'background:rgba(255,255,255,0.06);color:#b8d4ff;' +
      `">${t.label}</button>`
    ).join('');

    return /* html */`
      <!-- Close button -->
      <button id="sl-close" style="
        position:absolute;top:18px;right:22px;z-index:10;
        background:rgba(255,255,255,0.15);border:none;border-radius:50%;
        width:38px;height:38px;font:700 18px system-ui;color:#fff;cursor:pointer;
        pointer-events:auto;
      ">✕</button>

      <!-- Title -->
      <div style="
        position:absolute;top:20px;left:50%;transform:translateX(-50%);
        font:900 14px system-ui;letter-spacing:0.3em;
        background:linear-gradient(90deg,#7ef9ff,#9b59ff);
        -webkit-background-clip:text;-webkit-text-fill-color:transparent;
        pointer-events:auto;
      ">LOCKER</div>

      <!-- Left sidebar: tabs and item grid combined -->
      <div style="
        width:480px;min-width:440px;padding:64px 20px 24px;
        background:rgba(8,10,18,0.85);backdrop-filter:blur(12px);
        border-right:1px solid rgba(255,255,255,0.12);
        display:flex;flex-direction:row;gap:16px;
        pointer-events:auto;
      ">
        <!-- Tabs Column -->
        <div style="width:130px;display:flex;flex-direction:column;">
          ${tabsHtml}
        </div>
        <!-- Grid Column -->
        <div style="flex:1;overflow-y:auto;padding-right:4px;">
          <div id="sl-grid" style="
            display:grid;grid-template-columns:1fr 1fr;
            gap:12px;
          "></div>
        </div>
      </div>

      <!-- Center Spacer: fully transparent and pass-through so you can click & drag the 3D character -->
      <div style="flex:1;pointer-events:none;"></div>

      <!-- Right Panel: preview + equip -->
      <div style="
        width:240px;min-width:200px;padding:64px 20px 20px;
        background:rgba(8,10,18,0.85);backdrop-filter:blur(12px);
        border-left:1px solid rgba(255,255,255,0.12);
        display:flex;flex-direction:column;align-items:center;gap:18px;
        pointer-events:auto;
      ">
        <div style="font:700 11px system-ui;letter-spacing:0.14em;color:#9ec4ff;text-transform:uppercase">Preview</div>
        <div id="sl-preview" style="
          width:150px;height:190px;border-radius:12px;
          background:rgba(255,255,255,0.05);
          border:2px solid rgba(255,255,255,0.12);
          display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;
          padding:14px;text-align:center;
        "></div>
        <button id="sl-equip" style="
          width:100%;padding:14px 0;border:none;border-radius:10px;
          font:800 13px system-ui;letter-spacing:0.1em;cursor:pointer;
          background:linear-gradient(180deg,#ffe566,#ffc800);color:#1a1200;
          box-shadow:0 3px 0 #c97800;
        ">EQUIP</button>
      </div>
    `;
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  private bindEvents(): void {
    // Close button
    (this.overlay.querySelector('#sl-close') as HTMLButtonElement)
      .addEventListener('click', () => this.hide());

    // Esc key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.visible) this.hide();
    });

    // Category tabs
    this.overlay.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.activeCategory = btn.dataset['cat'] as SkinCategory;
        this.renderGrid();
        this.renderPreview();
        this.updateTabStyles();
      });
    });

    // Equip button
    this.equipBtn.addEventListener('click', () => this.equipPending());
  }

  // ---------------------------------------------------------------------------
  // Render grid
  // ---------------------------------------------------------------------------

  private renderGrid(): void {
    this.gridEl.innerHTML = '';

    const items = this.itemsForCategory(this.activeCategory);
    const equippedId = this.equippedIdForCategory(this.activeCategory);
    const pendingId  = this.pendingIdForCategory(this.activeCategory);

    for (const item of items) {
      const card = document.createElement('div');
      const isEquipped = item.id === equippedId;
      const isPending  = item.id === pendingId;

      card.style.cssText = [
        'padding:12px 10px;border-radius:10px;cursor:pointer;text-align:center',
        'background:rgba(255,255,255,0.06)',
        isPending
          ? 'border:2px solid #ffd700;box-shadow:0 0 12px rgba(255,215,0,0.4)'
          : 'border:2px solid rgba(255,255,255,0.12)',
      ].join(';');

      const swatch = this.swatchForItem(item);

      card.innerHTML = `
        <div style="
          width:48px;height:48px;border-radius:8px;margin:0 auto 8px;
          background:${swatch};border:1px solid rgba(255,255,255,0.15);
        "></div>
        <div style="font:600 11px system-ui;color:#ddeeff;line-height:1.3">${(item as { label: string }).label}</div>
        ${isEquipped ? '<div style="margin-top:5px;font:700 9px system-ui;letter-spacing:0.12em;color:#ffd700">EQUIPPED</div>' : ''}
      `;

      card.addEventListener('click', () => {
        this.setPending(this.activeCategory, item.id);
        this.renderGrid();
        this.renderPreview();
      });

      this.gridEl.appendChild(card);
    }
  }

  // ---------------------------------------------------------------------------
  // Render preview
  // ---------------------------------------------------------------------------

  private renderPreview(): void {
    const pendingId  = this.pendingIdForCategory(this.activeCategory);
    const equippedId = this.equippedIdForCategory(this.activeCategory);
    const item       = this.itemsForCategory(this.activeCategory).find(i => i.id === pendingId)!;
    const swatch     = this.swatchForItem(item);
    const isEquipped = pendingId === equippedId;

    this.previewEl.innerHTML = `
      <div style="
        width:72px;height:72px;border-radius:10px;
        background:${swatch};border:1px solid rgba(255,255,255,0.2);
      "></div>
      <div style="font:700 12px system-ui;color:#fff">${(item as { label: string }).label}</div>
      ${isEquipped
        ? '<div style="padding:4px 10px;border-radius:999px;background:rgba(255,215,0,0.2);border:1px solid #ffd700;font:700 10px system-ui;letter-spacing:0.1em;color:#ffd700">EQUIPPED</div>'
        : ''}
    `;

    this.equipBtn.style.opacity = isEquipped ? '0.45' : '1';
    this.equipBtn.style.cursor  = isEquipped ? 'default' : 'pointer';
  }

  // ---------------------------------------------------------------------------
  // Tab styles
  // ---------------------------------------------------------------------------

  private updateTabStyles(): void {
    (['skin','wrap','pickaxe','glider'] as SkinCategory[]).forEach(cat => {
      const btn = this.overlay.querySelector<HTMLButtonElement>(`#sl-tab-${cat}`);
      if (!btn) return;
      if (cat === this.activeCategory) {
        btn.style.background = 'rgba(255,215,0,0.18)';
        btn.style.color = '#ffd700';
        btn.style.borderLeft = '3px solid #ffd700';
      } else {
        btn.style.background = 'rgba(255,255,255,0.06)';
        btn.style.color = '#b8d4ff';
        btn.style.borderLeft = 'none';
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Equip
  // ---------------------------------------------------------------------------

  private equipPending(): void {
    switch (this.activeCategory) {
      case 'skin':    localStorage.setItem(LS_SKIN,    this.pendingSkin);    break;
      case 'wrap':    localStorage.setItem(LS_WRAP,    this.pendingWrap);    break;
      case 'pickaxe': localStorage.setItem(LS_PICKAXE, this.pendingPickaxe); break;
      case 'glider':  localStorage.setItem(LS_GLIDER,  this.pendingGlider);  break;
    }
    this.renderGrid();
    this.renderPreview();
    this.loadoutCbs.forEach(cb => cb(this.getLoadout()));
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private itemsForCategory(cat: SkinCategory): Array<SkinItem | WrapItem | PickaxeItem | GliderItem> {
    switch (cat) {
      case 'skin':    return SKINS;
      case 'wrap':    return WRAPS;
      case 'pickaxe': return PICKAXES;
      case 'glider':  return GLIDERS;
    }
  }

  private equippedIdForCategory(cat: SkinCategory): string {
    switch (cat) {
      case 'skin':    return localStorage.getItem(LS_SKIN)    ?? 'default';
      case 'wrap':    return localStorage.getItem(LS_WRAP)    ?? 'default_wrap';
      case 'pickaxe': return localStorage.getItem(LS_PICKAXE) ?? 'default_axe';
      case 'glider':  return localStorage.getItem(LS_GLIDER)  ?? 'default_glider';
    }
  }

  private pendingIdForCategory(cat: SkinCategory): string {
    switch (cat) {
      case 'skin':    return this.pendingSkin;
      case 'wrap':    return this.pendingWrap;
      case 'pickaxe': return this.pendingPickaxe;
      case 'glider':  return this.pendingGlider;
    }
  }

  private setPending(cat: SkinCategory, id: string): void {
    switch (cat) {
      case 'skin':    this.pendingSkin    = id; break;
      case 'wrap':    this.pendingWrap    = id; break;
      case 'pickaxe': this.pendingPickaxe = id; break;
      case 'glider':  this.pendingGlider  = id; break;
    }
  }

  private swatchForItem(item: SkinItem | WrapItem | PickaxeItem | GliderItem): string {
    if ('hue'   in item) return hslFromHue(item.hue);
    if ('color' in item) return item.color;
    if ('swatch' in item) return (item as PickaxeItem | GliderItem).swatch;
    return '#888';
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    // Re-sync pending selections to equipped on open
    this.pendingSkin    = localStorage.getItem(LS_SKIN)    ?? 'default';
    this.pendingWrap    = localStorage.getItem(LS_WRAP)    ?? 'default_wrap';
    this.pendingPickaxe = localStorage.getItem(LS_PICKAXE) ?? 'default_axe';
    this.pendingGlider  = localStorage.getItem(LS_GLIDER)  ?? 'default_glider';
    this.activeCategory = 'skin';
    this.updateTabStyles();
    this.renderGrid();
    this.renderPreview();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
  }

  isVisible(): boolean {
    return this.visible;
  }

  getLoadout(): SkinLoadout {
    return {
      skinId:    localStorage.getItem(LS_SKIN)    ?? 'default',
      wrapId:    localStorage.getItem(LS_WRAP)    ?? 'default_wrap',
      pickaxeId: localStorage.getItem(LS_PICKAXE) ?? 'default_axe',
      gliderId:  localStorage.getItem(LS_GLIDER)  ?? 'default_glider',
    };
  }

  /** Returns 0–1 hue for current equipped skin (for THREE.Color.setHSL) */
  getSkinHue(): number {
    const id = localStorage.getItem(LS_SKIN) ?? 'default';
    return SKINS.find(s => s.id === id)?.hue ?? 0;
  }

  /** Returns hex color string for current equipped wrap */
  getWrapColor(): string {
    const id = localStorage.getItem(LS_WRAP) ?? 'default_wrap';
    return WRAPS.find(w => w.id === id)?.color ?? '#888888';
  }

  onLoadoutChange(cb: (loadout: SkinLoadout) => void): void {
    this.loadoutCbs.push(cb);
  }
}
